import React from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown } from 'lucide-react';

// ── Card ────────────────────────────────────────────────────────────────────
export function Card({ children, className = '', style = {} }) {
  return (
    <div className={`card ${className}`} style={{
      background: 'var(--bg-card)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      padding: '20px',
      ...style
    }}>
      {children}
    </div>
  );
}

// ── Stat Card ────────────────────────────────────────────────────────────────
export function StatCard({ label, value, icon: Icon, color = 'var(--accent-blue)', sub }) {
  return (
    <Card style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 20 }}>
      <div style={{
        width: 44, height: 44, borderRadius: 10,
        background: `${color}22`,
        border: `1px solid ${color}44`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Icon size={20} color={color} />
      </div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 600, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
          {value ?? '—'}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
      </div>
    </Card>
  );
}

// ── Button ───────────────────────────────────────────────────────────────────
export function Button({ children, variant = 'primary', size = 'md', disabled, loading, onClick, style = {}, className = '' }) {
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    border: 'none', cursor: disabled || loading ? 'not-allowed' : 'pointer',
    fontFamily: 'var(--font-sans)', fontWeight: 500,
    transition: 'all 0.15s', borderRadius: 'var(--radius)',
    opacity: disabled || loading ? 0.6 : 1,
    ...style,
  };
  const sizes = {
    sm: { padding: '5px 10px', fontSize: 12 },
    md: { padding: '8px 14px', fontSize: 13 },
    lg: { padding: '10px 20px', fontSize: 14 },
  };
  const variants = {
    primary: { background: 'var(--accent-blue)', color: '#fff' },
    secondary: { background: 'var(--bg-card-hover)', color: 'var(--text-primary)', border: '1px solid var(--border)' },
    danger: { background: 'var(--accent-red)', color: '#fff' },
    ghost: { background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border)' },
    success: { background: 'var(--accent-green)', color: '#fff' },
  };
  return (
    <button
      style={{ ...base, ...sizes[size], ...variants[variant] }}
      className={className}
      disabled={disabled || loading}
      onClick={onClick}
    >
      {loading && <Spinner size={12} />}
      {children}
    </button>
  );
}

// ── Badge ────────────────────────────────────────────────────────────────────
export function Badge({ status }) {
  const map = {
    active: 'badge-active', inactive: 'badge-inactive',
    suspended: 'badge-suspended', expired: 'badge-expired',
    success: 'badge-success', error: 'badge-error',
    pending: 'badge-pending', synced: 'badge-synced',
  };
  return <span className={`badge ${map[status] || 'badge-inactive'}`}>{status}</span>;
}

// ── Input ────────────────────────────────────────────────────────────────────
export function Input({ label, error, disabled, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {label && <label style={{ fontSize: 12, color: disabled ? 'var(--text-muted)' : 'var(--text-secondary)', fontWeight: 500 }}>{label}</label>}
      <input
        disabled={disabled}
        style={{
          background: disabled ? 'var(--bg-secondary)' : 'var(--bg-input)',
          border: `1px solid ${error ? 'var(--accent-red)' : 'var(--border)'}`,
          borderRadius: 'var(--radius)', padding: '8px 12px',
          color: disabled ? 'var(--text-muted)' : 'var(--text-primary)',
          fontSize: 13, outline: 'none', width: '100%',
          fontFamily: 'var(--font-sans)',
          cursor: disabled ? 'not-allowed' : 'text',
          opacity: disabled ? 0.6 : 1,
        }}
        {...props}
      />
      {error && <span style={{ fontSize: 11, color: 'var(--accent-red)' }}>{error}</span>}
    </div>
  );
}

// ── Textarea ──────────────────────────────────────────────────────────────────
export function Textarea({ label, error, rows = 4, disabled, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {label && <label style={{ fontSize: 12, color: disabled ? 'var(--text-muted)' : 'var(--text-secondary)', fontWeight: 500 }}>{label}</label>}
      <textarea
        rows={rows}
        disabled={disabled}
        style={{
          background: disabled ? 'var(--bg-secondary)' : 'var(--bg-input)',
          border: `1px solid ${error ? 'var(--accent-red)' : 'var(--border)'}`,
          borderRadius: 'var(--radius)', padding: '8px 12px',
          color: disabled ? 'var(--text-muted)' : 'var(--text-primary)',
          fontSize: 13, outline: 'none', width: '100%',
          fontFamily: 'var(--font-sans)', resize: 'vertical', lineHeight: 1.5,
          cursor: disabled ? 'not-allowed' : 'text',
          opacity: disabled ? 0.6 : 1,
        }}
        {...props}
      />
      {error && <span style={{ fontSize: 11, color: 'var(--accent-red)' }}>{error}</span>}
    </div>
  );
}

// ── Select ───────────────────────────────────────────────────────────────────
export function Select({ label, children, error, disabled, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {label && <label style={{ fontSize: 12, color: disabled ? 'var(--text-muted)' : 'var(--text-secondary)', fontWeight: 500 }}>{label}</label>}
      <select
        disabled={disabled}
        style={{
          background: disabled ? 'var(--bg-secondary)' : 'var(--bg-input)',
          border: `1px solid ${error ? 'var(--accent-red)' : 'var(--border)'}`,
          borderRadius: 'var(--radius)', padding: '8px 12px',
          color: disabled ? 'var(--text-muted)' : 'var(--text-primary)',
          fontSize: 13, outline: 'none', width: '100%',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
        {...props}
      >
        {children}
      </select>
    </div>
  );
}

// ── SearchSelect ─────────────────────────────────────────────────────────────
// Single-value combobox dengan search. Default (belum ngetik) cuma nampilin
// `defaultCount` item pertama biar list panjang (tenant/profile) gak berat.
export function SearchSelect({
  label, value, onChange, options, placeholder = 'Pilih...', defaultCount = 10,
  error, disabled, getKey = o => o.id, getLabel = o => o.label, getSub = o => o.sub,
  allowClear = false, clearLabel = '— Kosongkan —', buttonStyle = {},
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [pos, setPos] = React.useState({ top: 0, left: 0, width: 0 });
  const wrapRef = React.useRef(null);
  const dropdownRef = React.useRef(null);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      // Dropdown-nya di-portal ke document.body, jadi bukan descendant dari wrapRef secara DOM —
      // harus dicek juga ke dropdownRef, kalau enggak klik di dalam dropdown dianggap "di luar".
      if (wrapRef.current?.contains(e.target)) return;
      if (dropdownRef.current?.contains(e.target)) return;
      setOpen(false); setQuery('');
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const openDropdown = () => {
    if (disabled) return;
    const rect = wrapRef.current?.getBoundingClientRect();
    if (rect) setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? options.filter(o => getLabel(o).toLowerCase().includes(q) || (getSub(o) || '').toLowerCase().includes(q))
    : options;
  const visible = filtered.slice(0, q ? 30 : defaultCount);
  const selected = options.find(o => getKey(o) === value);

  const pick = (o) => { onChange(o ? getKey(o) : ''); setOpen(false); setQuery(''); };

  return (
    <div ref={wrapRef} style={{ display: 'flex', flexDirection: 'column', gap: 4, position: 'relative' }}>
      {label && <label style={{ fontSize: 12, color: disabled ? 'var(--text-muted)' : 'var(--text-secondary)', fontWeight: 500 }}>{label}</label>}
      <button type="button" disabled={disabled} onClick={() => (open ? setOpen(false) : openDropdown())}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          background: disabled ? 'var(--bg-secondary)' : 'var(--bg-input)',
          border: `1px solid ${error ? 'var(--accent-red)' : open ? 'var(--accent-blue)' : 'var(--border)'}`,
          borderRadius: 'var(--radius)', padding: '8px 12px', width: '100%', textAlign: 'left',
          color: selected ? 'var(--text-primary)' : 'var(--text-muted)', fontSize: 13,
          cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1, fontFamily: 'var(--font-sans)',
          ...buttonStyle,
        }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? getLabel(selected) : placeholder}
        </span>
        <ChevronDown size={13} style={{ flexShrink: 0, opacity: 0.6, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>

      {open && !disabled && ReactDOM.createPortal(
        <div ref={dropdownRef} style={{
          position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 9998,
          background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.35)', overflow: 'hidden', animation: 'fadeIn .12s ease-out',
        }}>
          <div style={{ padding: 8, borderBottom: '1px solid var(--border)' }}>
            <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Cari..." style={{
                width: '100%', background: 'var(--bg-input)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius)', padding: '6px 10px', color: 'var(--text-primary)',
                fontSize: 13, outline: 'none', fontFamily: 'var(--font-sans)', boxSizing: 'border-box',
              }} />
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto', padding: 4 }}>
            {allowClear && (
              <button type="button" onClick={() => pick(null)} style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px',
                background: !selected ? 'rgba(59,130,246,0.1)' : 'none', border: 'none', borderRadius: 'var(--radius)',
                color: !selected ? 'var(--accent-blue)' : 'var(--text-muted)', fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font-sans)',
              }}>{clearLabel}</button>
            )}
            {visible.length === 0 ? (
              <div style={{ padding: '10px 8px', fontSize: 12, color: 'var(--text-muted)' }}>
                {options.length === 0 ? 'Tidak ada data.' : 'Tidak ditemukan.'}
              </div>
            ) : visible.map(o => (
              <button type="button" key={getKey(o)} onClick={() => pick(o)} style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px',
                background: getKey(o) === value ? 'rgba(59,130,246,0.1)' : 'none', border: 'none', borderRadius: 'var(--radius)',
                color: getKey(o) === value ? 'var(--accent-blue)' : 'var(--text-primary)', fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font-sans)',
                transition: 'background 0.1s',
              }}
                onMouseEnter={e => { if (getKey(o) !== value) e.currentTarget.style.background = 'var(--bg-card-hover)'; }}
                onMouseLeave={e => { if (getKey(o) !== value) e.currentTarget.style.background = 'none'; }}
              >
                {getLabel(o)} {getSub(o) && <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>({getSub(o)})</span>}
              </button>
            ))}
            {!q && options.length > defaultCount && (
              <div style={{ padding: '6px 10px 2px', fontSize: 11, color: 'var(--text-muted)' }}>
                Menampilkan {defaultCount} dari {options.length} — ketik untuk cari yang lain.
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
      {error && <span style={{ fontSize: 11, color: 'var(--accent-red)' }}>{error}</span>}
    </div>
  );
}

// ── TagSelect ────────────────────────────────────────────────────────────────
// Multi-select bergaya tag/chip dengan search (mirip model tags di NetBox).
// `maxTags` opsional buat batasi jumlah pilihan (mis. role user cuma boleh 1 tenant).
export function TagSelect({
  label, value = [], onChange, options, placeholder = 'Cari & pilih...', maxTags,
  error, disabled, getKey = o => o.id, getLabel = o => o.label, getSub = o => o.sub, hint,
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [pos, setPos] = React.useState({ top: 0, left: 0, width: 0 });
  const wrapRef = React.useRef(null);
  const dropdownRef = React.useRef(null);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (dropdownRef.current?.contains(e.target)) return;
      setOpen(false); setQuery('');
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const openDropdown = () => {
    if (disabled) return;
    const rect = wrapRef.current?.getBoundingClientRect();
    if (rect) setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    setOpen(true);
  };

  const selectedItems = value.map(id => options.find(o => getKey(o) === id)).filter(Boolean);
  const available = options.filter(o => !value.includes(getKey(o)));
  const q = query.trim().toLowerCase();
  const filtered = q
    ? available.filter(o => getLabel(o).toLowerCase().includes(q) || (getSub(o) || '').toLowerCase().includes(q))
    : available;
  const visible = filtered.slice(0, 30);
  const atMax = !!maxTags && value.length >= maxTags;
  const canType = !disabled && !atMax;

  const addTag = (o) => {
    if (atMax) return;
    onChange([...value, getKey(o)]);
    setQuery('');
    inputRef.current?.focus();
  };
  const removeTag = (id) => onChange(value.filter(v => v !== id));
  const handleKeyDown = (e) => {
    if (e.key === 'Backspace' && !query && value.length > 0) removeTag(value[value.length - 1]);
  };

  return (
    <div ref={wrapRef} style={{ display: 'flex', flexDirection: 'column', gap: 4, position: 'relative' }}>
      {label && <label style={{ fontSize: 12, color: disabled ? 'var(--text-muted)' : 'var(--text-secondary)', fontWeight: 500 }}>{label}</label>}
      <div onClick={() => canType && !open && openDropdown()}
        style={{
          display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
          background: disabled ? 'var(--bg-secondary)' : 'var(--bg-input)',
          border: `1px solid ${error ? 'var(--accent-red)' : open ? 'var(--accent-blue)' : 'var(--border)'}`,
          borderRadius: 'var(--radius)', padding: '6px 8px', minHeight: 38,
          cursor: disabled ? 'not-allowed' : canType ? 'text' : 'default', opacity: disabled ? 0.6 : 1,
        }}>
        {selectedItems.map(o => (
          <span key={getKey(o)} style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 6px 3px 10px',
            background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)',
            borderRadius: 20, fontSize: 12, color: 'var(--accent-blue)', fontWeight: 500,
          }}>
            {getLabel(o)}
            {!disabled && (
              <button type="button" onClick={(e) => { e.stopPropagation(); removeTag(getKey(o)); }}
                style={{ background: 'none', border: 'none', color: 'var(--accent-blue)', cursor: 'pointer', padding: 0, lineHeight: 1, fontSize: 15, opacity: 0.75 }}>
                ×
              </button>
            )}
          </span>
        ))}
        {canType && (
          <input ref={inputRef} value={query}
            onChange={e => { setQuery(e.target.value); if (!open) openDropdown(); }}
            onFocus={() => !open && openDropdown()}
            onKeyDown={handleKeyDown}
            placeholder={selectedItems.length === 0 ? placeholder : ''}
            style={{
              flex: 1, minWidth: 90, background: 'none', border: 'none', outline: 'none',
              color: 'var(--text-primary)', fontSize: 13, fontFamily: 'var(--font-sans)', padding: '3px 2px',
            }} />
        )}
      </div>
      {hint && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{hint}</span>}
      {error && <span style={{ fontSize: 11, color: 'var(--accent-red)' }}>{error}</span>}

      {open && canType && ReactDOM.createPortal(
        <div ref={dropdownRef} style={{
          position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 9998,
          background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.35)', overflow: 'hidden', animation: 'fadeIn .12s ease-out',
        }}>
          <div style={{ maxHeight: 220, overflowY: 'auto', padding: 4 }}>
            {visible.length === 0 ? (
              <div style={{ padding: '10px 8px', fontSize: 12, color: 'var(--text-muted)' }}>
                {options.length === 0 ? 'Tidak ada data.' : available.length === 0 ? 'Semua sudah dipilih.' : 'Tidak ditemukan.'}
              </div>
            ) : visible.map(o => (
              <button type="button" key={getKey(o)} onClick={() => addTag(o)} style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px',
                background: 'none', border: 'none', borderRadius: 'var(--radius)',
                color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font-sans)',
                transition: 'background 0.1s',
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover)'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}
              >
                {getLabel(o)} {getSub(o) && <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>({getSub(o)})</span>}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ── Spinner ──────────────────────────────────────────────────────────────────
export function Spinner({ size = 20, color = 'var(--accent-blue)' }) {
  return (
    <div style={{
      width: size, height: size, border: `2px solid ${color}33`,
      borderTopColor: color, borderRadius: '50%',
      animation: 'spin 0.7s linear infinite', flexShrink: 0,
    }} />
  );
}

// ── Empty State ───────────────────────────────────────────────────────────────
export function Empty({ message = 'Tidak ada data', icon: Icon }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 16px', color: 'var(--text-muted)' }}>
      {Icon && <Icon size={32} style={{ marginBottom: 12, opacity: 0.4 }} />}
      <div style={{ fontSize: 13 }}>{message}</div>
    </div>
  );
}

// ── Modal ────────────────────────────────────────────────────────────────────
export function Modal({ open, onClose, title, children, width = 520 }) {
  if (!open) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="animate-in modal-inner" style={{
        background: 'var(--bg-secondary)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: width,
        maxHeight: '90vh', overflow: 'auto', boxShadow: 'var(--shadow)',
      }}>
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{ fontWeight: 600, fontSize: 15 }}>{title}</span>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: 'var(--text-muted)',
            fontSize: 18, cursor: 'pointer', lineHeight: 1,
          }}>×</button>
        </div>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}

// ── Table ────────────────────────────────────────────────────────────────────
// ── useSort ──────────────────────────────────────────────────────────────────
// Helper sort state buat dipasangin ke <Table sort=... onSortChange=... />.
// `sortParam` = string siap kirim ke backend: 'id' (asc) / '-id' (desc) / undefined.
// Klik kolom yang sama: asc -> desc -> (unsorted). Klik kolom lain: langsung asc.
export function useSort(defaultKey = null, defaultDir = 'asc') {
  const [sort, setSort] = React.useState(defaultKey ? { key: defaultKey, dir: defaultDir } : null);
  const onSortChange = (key) => {
    setSort(prev => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  };
  const resetSort = () => setSort(null);
  const sortParam = sort ? (sort.dir === 'desc' ? `-${sort.key}` : sort.key) : undefined;
  return { sort, onSortChange, resetSort, sortParam };
}

export function Table({ columns, data, loading, emptyMessage, selected, onSelect, renderExpanded, expandedId: controlledExpandedId, sort, onSortChange }) {
  const hasSelect = !!onSelect;
  const hasExpand = !!renderExpanded;
  const [internalExpandedId, setInternalExpandedId] = React.useState(null);
  const expandedId = controlledExpandedId !== undefined ? controlledExpandedId : internalExpandedId;
  const setExpandedId = controlledExpandedId !== undefined ? () => {} : setInternalExpandedId;
  const allIds = data?.map(r => r.id).filter(Boolean) || [];
  const allChecked = allIds.length > 0 && allIds.every(id => selected?.includes(id));
  const someChecked = !allChecked && allIds.some(id => selected?.includes(id));
  const toggleAll = () => onSelect(allChecked ? [] : allIds);
  const toggleOne = (id) => onSelect(selected?.includes(id) ? selected.filter(x => x !== id) : [...(selected || []), id]);
  const cbStyle = { width: 15, height: 15, cursor: 'pointer', accentColor: 'var(--accent-blue)' };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)' }}>
            {hasSelect && (
              <th style={{ padding: '10px 12px', width: 36 }}>
                <input type="checkbox" style={cbStyle} checked={allChecked}
                  ref={el => { if (el) el.indeterminate = someChecked; }}
                  onChange={toggleAll} />
              </th>
            )}
            {columns.map(col => {
              const isSortable = !!col.sortable && !!onSortChange;
              const active = isSortable && sort?.key === (col.sortKey || col.key);
              return (
                <th key={col.key} style={{
                  padding: '10px 12px', textAlign: 'left',
                  color: active ? 'var(--text-primary)' : 'var(--text-muted)', fontSize: 11, fontWeight: 600,
                  textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap',
                }}>
                  {isSortable ? (
                    <button
                      onClick={() => onSortChange(col.sortKey || col.key)}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, font: 'inherit', color: 'inherit', textTransform: 'inherit', letterSpacing: 'inherit' }}
                    >
                      {col.label}
                      <span style={{ fontSize: 9, opacity: active ? 1 : 0.35 }}>
                        {active ? (sort.dir === 'desc' ? '▼' : '▲') : '⇅'}
                      </span>
                    </button>
                  ) : col.label}
                </th>
              );
            })}
            {hasExpand && <th style={{ width: 36 }} />}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <tr key={i}>
                {hasSelect && <td style={{ padding: '10px 12px' }}><div className="skeleton" style={{ height: 15, width: 15 }} /></td>}
                {columns.map(col => (
                  <td key={col.key} style={{ padding: '10px 12px' }}>
                    <div className="skeleton" style={{ height: 16, width: '80%' }} />
                  </td>
                ))}
              </tr>
            ))
          ) : data?.length === 0 ? (
            <tr><td colSpan={columns.length + (hasSelect ? 1 : 0)}><Empty message={emptyMessage} /></td></tr>
          ) : (
            data?.map((row, i) => {
              const isSel = selected?.includes(row.id);
              const isExp = expandedId === row.id;
              return (
              <React.Fragment key={row.id ?? i}>
                <tr style={{
                  borderBottom: isExp ? 'none' : '1px solid var(--border)', transition: 'background 0.1s',
                  background: isSel ? 'rgba(59,130,246,0.08)' : '',
                }}
                  onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = 'var(--bg-card-hover)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = isSel ? 'rgba(59,130,246,0.08)' : ''; }}
                >
                  {hasSelect && (
                    <td style={{ padding: '10px 12px', width: 36 }}>
                      <input type="checkbox" style={cbStyle} checked={!!isSel} onChange={() => toggleOne(row.id)} />
                    </td>
                  )}
                  {columns.map(col => (
                    <td key={col.key} style={{ padding: '10px 12px', color: 'var(--text-secondary)' }}>
                      {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                    </td>
                  ))}
                </tr>
                {hasExpand && isExp && (
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <td colSpan={columns.length + (hasSelect ? 1 : 0) + 1} style={{ padding: '0 16px 14px 44px', background: 'var(--bg-input)' }}>
                      {renderExpanded(row)}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            )})
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Pagination ───────────────────────────────────────────────────────────────
export function Pagination({ page, totalPages, total, onChange, perPage, onPerPageChange, perPageOptions = [10, 15, 20, 50, 100] }) {
  if (!totalPages) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', gap: 8, flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {onPerPageChange && (
          <>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Per page:</span>
            <select value={perPage} onChange={e => { onPerPageChange(Number(e.target.value)); onChange(1); }}
              style={{ fontSize: 12, padding: '3px 8px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-secondary)', cursor: 'pointer' }}>
              {perPageOptions.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </>
        )}
        {total != null && (
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {total} total
          </span>
        )}
      </div>
      {totalPages > 1 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => onChange(1)}>«</Button>
          <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>‹ Prev</Button>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', minWidth: 60, textAlign: 'center' }}>
            {page} / {totalPages}
          </span>
          <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>Next ›</Button>
          <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => onChange(totalPages)}>»</Button>
        </div>
      ) : <div />}
    </div>
  );
}

// ── Alert ─────────────────────────────────────────────────────────────────────
export function Alert({ type = 'error', message, onClose }) {
  if (!message) return null;
  const colors = {
    error: { bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.3)', text: 'var(--accent-red)' },
    success: { bg: 'rgba(16,185,129,0.1)', border: 'rgba(16,185,129,0.3)', text: 'var(--accent-green)' },
    warning: { bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.3)', text: 'var(--accent-orange)' },
  };
  const c = colors[type];
  return (
    <div style={{
      background: c.bg, border: `1px solid ${c.border}`, color: c.text,
      borderRadius: 'var(--radius)', padding: '10px 14px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
      fontSize: 13, marginBottom: 12,
    }}>
      <span>{message}</span>
      {onClose && <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 16 }}>×</button>}
    </div>
  );
}

// ── Page Header ───────────────────────────────────────────────────────────────
export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="page-header-wrap" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, gap: 16 }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>{title}</h1>
        {subtitle && <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>{subtitle}</p>}
      </div>
      {actions && <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>{actions}</div>}
    </div>
  );
}